import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { MutationCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { AuthProvider } from "@/context/AuthContext";
import { IntentProvider } from "@/context/IntentContext";
import { CeremonyProvider } from "@/context/CeremonyContext";
import { IntentSelectorModal } from "@/components/intent/IntentSelectorModal";
import { ChatWidget } from "@/components/chat/ChatWidget";
import { ErrorToaster, showErrorToast } from "@/components/ui/ErrorToast";
import { ProtectedRoute, AdminRoute } from "@/components/layout/ProtectedRoute";
import { PremiumRoute, ShowRequiredRoute } from "@/components/layout/PremiumRoute";
import { AppShell } from "@/components/layout/AppShell";

// Eagerly loaded (landing + auth are entry points)
import { LandingPage } from "@/pages/LandingPage";
import { LoginPage } from "@/pages/LoginPage";
import { VerifyPage } from "@/pages/VerifyPage";

// Fringe curtain landing page
import { FringeLandingPage } from "@/pages/FringeLandingPage";
import { FringeGetStartedPage } from "@/pages/FringeGetStartedPage";

// Retry wrapper for lazy imports — handles stale chunk hashes after deploys
function lazyRetry<T extends { [key: string]: any }>(
  factory: () => Promise<T>,
  name: keyof T
) {
  return lazy(() =>
    factory()
      .then(m => ({ default: m[name] as any }))
      .catch(() => {
        // Chunk load failed — force reload to get new index.html with correct hashes
        window.location.reload();
        return new Promise(() => {});
      })
  );
}

// V1 (previous design) for A/B comparison — lazy so ~1,500 lines stay out of
// the main bundle. Delete these + the /1 alias routes once the A/B test ends.
const LandingPageV1 = lazyRetry(() => import("@/pages/LandingPageV1"), "LandingPageV1");
const LoginPageV1 = lazyRetry(() => import("@/pages/LoginPageV1"), "LoginPageV1");
const FeaturesPageV1 = lazyRetry(() => import("@/pages/FeaturesPageV1"), "FeaturesPageV1");
const HowItWorksPageV1 = lazyRetry(() => import("@/pages/HowItWorksPageV1"), "HowItWorksPageV1");

const DashboardPage = lazyRetry(() => import("@/pages/DashboardPage"), "DashboardPage");
const UpcomingShowsPage = lazyRetry(() => import("@/pages/UpcomingShowsPage"), "UpcomingShowsPage");
const FeaturesPage = lazyRetry(() => import("@/pages/FeaturesPage"), "FeaturesPage");
const HowItWorksPage = lazyRetry(() => import("@/pages/HowItWorksPage"), "HowItWorksPage");
const ResetPasswordPage = lazyRetry(() => import("@/pages/ResetPasswordPage"), "ResetPasswordPage");
const SalesReportPage = lazyRetry(() => import("@/pages/reports/SalesReportPage"), "SalesReportPage");
const DensityPage = lazyRetry(() => import("@/pages/reports/DensityPage"), "DensityPage");
const FinancialsPage = lazyRetry(() => import("@/pages/reports/FinancialsPage"), "FinancialsPage");
const RecommendationsPage = lazyRetry(() => import("@/pages/reports/RecommendationsPage"), "RecommendationsPage");
const ShowManagementPage = lazyRetry(() => import("@/pages/user/ShowManagementPage"), "ShowManagementPage");
const NewShowSetupPage = lazyRetry(() => import("@/pages/user/NewShowSetupPage"), "NewShowSetupPage");
const UsersPage = lazyRetry(() => import("@/pages/admin/UsersPage"), "UsersPage");
const ShowsPage = lazyRetry(() => import("@/pages/admin/ShowsPage"), "ShowsPage");
const FeedsPage = lazyRetry(() => import("@/pages/admin/FeedsPage"), "FeedsPage");
const ActivityPage = lazyRetry(() => import("@/pages/admin/ActivityPage"), "ActivityPage");
const TemplatesPage = lazyRetry(() => import("@/pages/admin/TemplatesPage"), "TemplatesPage");
const MissionsPage = lazyRetry(() => import("@/pages/MissionsPage"), "MissionsPage");
const ProfilePage = lazyRetry(() => import("@/pages/ProfilePage"), "ProfilePage");
const MissionsAdminPage = lazyRetry(() => import("@/pages/admin/MissionsAdminPage"), "MissionsAdminPage");
const GamificationAdminPage = lazyRetry(() => import("@/pages/admin/GamificationAdminPage"), "GamificationAdminPage");
const FestivalsPage = lazyRetry(() => import("@/pages/admin/FestivalsPage"), "FestivalsPage");
const AssetListPage = lazyRetry(() => import("@/pages/admin/AssetListPage"), "AssetListPage");
const PremiumCheckoutPage = lazyRetry(() => import("@/pages/PremiumCheckoutPage"), "PremiumCheckoutPage");
const PremiumSuccessPage = lazyRetry(() => import("@/pages/PremiumSuccessPage"), "PremiumSuccessPage");
const SeasonReportPage2 = lazyRetry(() => import("@/pages/reports/SeasonReportPage"), "SeasonReportPage");
const WeeklyCheckinPage = lazyRetry(() => import("@/pages/reports/WeeklyCheckinPage"), "WeeklyCheckinPage");
const ReadinessPage = lazyRetry(() => import("@/pages/reports/ReadinessPage"), "ReadinessPage");
const GoalsWizardPage = lazyRetry(() => import("@/pages/GoalsWizardPage"), "GoalsWizardPage");
const TrafficPage = lazyRetry(() => import("@/pages/admin/TrafficPage"), "TrafficPage");
const AnalyticsPage = lazyRetry(() => import("@/pages/admin/AnalyticsPage"), "AnalyticsPage");
const ComingSoonPage = lazyRetry(() => import("@/pages/ComingSoonPage"), "ComingSoonPage");
const NotificationsPage = lazyRetry(() => import("@/pages/NotificationsPage"), "NotificationsPage");
const LeaderboardPage = lazyRetry(() => import("@/pages/LeaderboardPage"), "LeaderboardPage");
const BadgeCasePage = lazyRetry(() => import("@/pages/BadgeCasePage"), "BadgeCasePage");
const AcademyPage = lazyRetry(() => import("@/pages/AcademyPage"), "AcademyPage");
const AcademyModulePage = lazyRetry(() => import("@/pages/AcademyModulePage"), "AcademyModulePage");
const AssetLibraryPage = lazyRetry(() => import("@/pages/AssetLibraryPage"), "AssetLibraryPage");
const ReferenceCenterPage = lazyRetry(() => import("@/pages/ReferenceCenterPage"), "ReferenceCenterPage");
const PublicProfilePage = lazyRetry(() => import("@/pages/PublicProfilePage"), "PublicProfilePage");

const queryClient = new QueryClient({
  // Safety net: any mutation without its own onError surfaces failures as a
  // toast instead of silently leaving the user with an unresponsive button.
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      if (mutation.options.onError) return;
      showErrorToast(error instanceof Error ? error.message : "Something went wrong. Please try again.");
    },
  }),
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function PageLoader() {
  return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500" />
    </div>
  );
}

import { Component, type ReactNode, type ErrorInfo } from "react";
class AppErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean; errorMsg: string }> {
  state = { hasError: false, errorMsg: "" };
  static getDerivedStateFromError(e: Error) { return { hasError: true, errorMsg: e?.message || "Unknown error" }; }
  componentDidCatch(e: Error, info: ErrorInfo) {
    console.error("[AppError]", e, info);
    try {
      fetch("/api/track", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          events: [{
            session_id: sessionStorage.getItem("smt_session_id") || null,
            event_type: "client_error",
            page_path: window.location.pathname,
            element_text: (e?.message || "Unknown").substring(0, 100),
            metadata: {
              stack: e?.stack?.substring(0, 500),
              componentStack: info?.componentStack?.substring(0, 500),
            },
          }],
        }),
      }).catch(() => {});
    } catch {}
  }
  render() {
    if (this.state.hasError) {
      const isChunkError = this.state.errorMsg.includes("Failed to fetch") || this.state.errorMsg.includes("Loading chunk") || this.state.errorMsg.includes("dynamically imported");
      return (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh", background: "#1e1f2c" }}>
          <div style={{ textAlign: "center", color: "#fafafc", maxWidth: "400px", padding: "20px" }}>
            <p style={{ fontSize: "16px", fontWeight: "bold", marginBottom: "8px" }}>
              {isChunkError ? "Page failed to load" : "Something went wrong"}
            </p>
            <p style={{ fontSize: "11px", color: "#8c8fa1", marginBottom: "16px" }}>{this.state.errorMsg}</p>
            <button onClick={() => { this.setState({ hasError: false, errorMsg: "" }); window.location.reload(); }}
              style={{ padding: "8px 20px", background: "#b87800", color: "#fff", border: "none", cursor: "pointer", fontSize: "13px" }}>
              Reload
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default function App() {
  return (
    <AppErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <CeremonyProvider>
        <IntentProvider>
          <BrowserRouter>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* Public marketing pages (V2 — current design) */}
              <Route path="/" element={<LandingPage />} />
              <Route path="/features" element={<FeaturesPage />} />
              <Route path="/how-it-works" element={<HowItWorksPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/signup" element={<Navigate to="/" replace />} />

              {/* V1 — previous design for comparison */}
              <Route path="/1" element={<LandingPageV1 />} />
              <Route path="/1/features" element={<FeaturesPageV1 />} />
              <Route path="/1/how-it-works" element={<HowItWorksPageV1 />} />
              <Route path="/1/login" element={<LoginPageV1 />} />

              {/* V2 — alias for current design */}
              <Route path="/2" element={<LandingPage />} />
              <Route path="/2/features" element={<FeaturesPage />} />
              <Route path="/2/how-it-works" element={<HowItWorksPage />} />
              <Route path="/2/login" element={<LoginPage />} />

              {/* V3 — taste-skill redesign */}
              <Route path="/3" element={<LandingPage />} />
              <Route path="/3/features" element={<FeaturesPage />} />
              <Route path="/3/how-it-works" element={<HowItWorksPage />} />
              <Route path="/3/login" element={<LoginPage />} />

              {/* Fringe curtain landing page */}
              <Route path="/fringe" element={<FringeLandingPage />} />
              <Route path="/fringe/getstarted" element={<FringeGetStartedPage />} />
              <Route path="/verify" element={<VerifyPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/profile/:username" element={<PublicProfilePage />} />

              {/* Protected app shell */}
              <Route element={<ProtectedRoute><AppShell /></ProtectedRoute>}>
                <Route path="/dashboard" element={<DashboardPage />} />
                <Route path="/upcoming-shows" element={<UpcomingShowsPage />} />

                {/* Season Complete pages — CS free, PS/IS blocked */}
                <Route path="/reports/sales" element={<PremiumRoute phases={["cs"]}><SalesReportPage /></PremiumRoute>} />
                <Route path="/reports/density" element={<PremiumRoute phases={["cs"]}><DensityPage /></PremiumRoute>} />
                <Route path="/reports/financials" element={<PremiumRoute phases={["cs"]}><FinancialsPage /></PremiumRoute>} />
                <Route path="/reports/recommendations" element={<PremiumRoute phases={["cs"]}><RecommendationsPage /></PremiumRoute>} />
                <Route path="/reports/season" element={<PremiumRoute phases={["cs"]}><SeasonReportPage2 /></PremiumRoute>} />
                <Route path="/reports/weekly" element={<PremiumRoute phases={["cs"]}><WeeklyCheckinPage /></PremiumRoute>} />

                {/* Pre-Season pages — premium-gated, PS only (+ premium IS/CS) */}
                <Route path="/reports/readiness" element={<PremiumRoute phases={["ps"]}><ReadinessPage /></PremiumRoute>} />
                <Route path="/goals" element={<ShowRequiredRoute><GoalsWizardPage /></ShowRequiredRoute>} />
                <Route path="/marketing" element={<PremiumRoute phases={["ps"]}><ComingSoonPage /></PremiumRoute>} />

                {/* In-Season pages — premium-gated, IS only (+ premium PS/CS) */}
                <Route path="/live-dashboard" element={<PremiumRoute phases={["is"]}><ComingSoonPage /></PremiumRoute>} />
                <Route path="/daily-tweaks" element={<PremiumRoute phases={["is"]}><ComingSoonPage /></PremiumRoute>} />
                <Route path="/extra-sales" element={<PremiumRoute phases={["is"]}><ComingSoonPage /></PremiumRoute>} />
                <Route path="/win-tracker" element={<PremiumRoute phases={["is"]}><ComingSoonPage /></PremiumRoute>} />

                {/* Cross-phase premium pages */}
                <Route path="/missions" element={<PremiumRoute phases={["ps", "is"]}><MissionsPage /></PremiumRoute>} />
                <Route path="/leaderboard" element={<PremiumRoute phases={["ps", "is", "cs"]}><LeaderboardPage /></PremiumRoute>} />
                <Route path="/academy" element={<PremiumRoute phases={["ps", "is", "cs"]}><AcademyPage /></PremiumRoute>} />
                <Route path="/academy/:moduleId" element={<PremiumRoute phases={["ps", "is", "cs"]}><AcademyModulePage /></PremiumRoute>} />
                <Route path="/assets" element={<PremiumRoute phases={["ps", "is", "cs"]}><AssetLibraryPage /></PremiumRoute>} />
                <Route path="/reference" element={<ReferenceCenterPage />} />
                <Route path="/badges" element={<PremiumRoute phases={["ps", "is", "cs"]}><BadgeCasePage /></PremiumRoute>} />

                {/* Premium checkout (always accessible) */}
                <Route path="/premium/checkout" element={<PremiumCheckoutPage />} />
                <Route path="/premium/success" element={<PremiumSuccessPage />} />

                {/* Always accessible — setup, profile, shows, notifications */}
                <Route path="/notifications" element={<NotificationsPage />} />
                <Route path="/profile" element={<ProfilePage />} />
                <Route path="/my-shows" element={<ShowManagementPage />} />
                <Route path="/new-show-setup" element={<NewShowSetupPage />} />

                {/* Admin pages */}
                <Route path="/admin/users" element={<AdminRoute><UsersPage /></AdminRoute>} />
                <Route path="/admin/shows" element={<AdminRoute><ShowsPage /></AdminRoute>} />
                <Route path="/admin/feeds" element={<AdminRoute><FeedsPage /></AdminRoute>} />
                <Route path="/admin/activity" element={<AdminRoute><ActivityPage /></AdminRoute>} />
                <Route path="/admin/templates" element={<AdminRoute><TemplatesPage /></AdminRoute>} />
                <Route path="/admin/traffic" element={<AdminRoute><TrafficPage /></AdminRoute>} />
                <Route path="/admin/analytics" element={<AdminRoute><AnalyticsPage /></AdminRoute>} />
                <Route path="/admin/missions" element={<AdminRoute><MissionsAdminPage /></AdminRoute>} />
                <Route path="/admin/gamification" element={<AdminRoute><GamificationAdminPage /></AdminRoute>} />
                <Route path="/admin/festivals" element={<AdminRoute><FestivalsPage /></AdminRoute>} />
                <Route path="/admin/assets" element={<AdminRoute><AssetListPage /></AdminRoute>} />
              </Route>

              {/* Default redirect to landing */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
          {/* Global Intent Selector Modal — single conversion mechanism for entire site */}
          <IntentSelectorModal />
          <ChatWidget />
          <ErrorToaster />
          </BrowserRouter>
        </IntentProvider>
        </CeremonyProvider>
      </AuthProvider>
    </QueryClientProvider>
    </AppErrorBoundary>
  );
}
